/**
 * Users (ARCHITECTURE.md "users/{uid}", "Cloud Functions").
 *
 * users/{uid} is written only by Cloud Functions. Reads are scoped like
 * loginEvents (admin: all; GM: their unit's members; others: own profile), so
 * useUsers() queries with userScopeConstraints() from lib/scope.ts.
 *
 *   const { users, loading, error } = useUsers()                  // live, in the viewer's scope, sorted by display name
 *   const { uid } = await adminCreateUser({ username, password, displayName, role: 'staff', companyId, ... })
 *   await adminUpdateUser({ uid, displayName, companyId, administrationId, departmentId, password? })
 *   await adminResetVerification(uid)                             // = adminUpdateUser({ uid, resetVerification: true })
 *   await adminMarkVerified(uid)                                  // Super Admin: = adminUpdateUser({ uid, markVerified: true })
 *   await adminSetUserActive({ uid, active: false })
 *   await adminDeleteUser(uid)                                    // employees.delete; records keep the stored name
 *
 * Callable failures are FunctionsError with `details: { reason, field? }`;
 * read them with callableErrorDetails() and map `reason` to a message.
 */
import type { OrgPlacement } from "../types";
import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, type FirestoreError } from "firebase/firestore";
import { useAuth } from "../auth";
import { callable, db, SNAPSHOT_OPTIONS } from "../firebase";
import { useI18n } from "../i18n";
import { scopeKey, userScopeConstraints } from "../scope";
import { COLLECTIONS, type Role, type UserProfile, type UserRow } from "../types";

export type { UserRow };

/** Limits enforced by the callables (mirror functions/src/config.ts). */
export { DISPLAY_NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "../auth-shared";
export const JOB_TITLE_MAX_LENGTH = 100;
export const PHONE_MAX_LENGTH = 32;
export const STAFF_NUMBER_MAX_LENGTH = 30;
/** Digits, spaces and + - ( ) only (empty allowed). */
export const PHONE_PATTERN = /^[0-9+\-\s()]*$/;

// ---------------------------------------------------------------------------
// Live list
// ---------------------------------------------------------------------------

export interface UseUsersOptions {
  /** Only active users (default false: everyone). */
  activeOnly?: boolean;
}

/**
 * Users in the viewer's scope, live, sorted by display name in the current
 * language's collation. Admins get everyone; a GM gets the members of their
 * unit (names outside it cannot be resolved; callers show a placeholder).
 */
export function useUsers(opts?: UseUsersOptions): {
  users: UserRow[];
  loading: boolean;
  error: FirestoreError | null;
} {
  const { locale } = useI18n();
  const { claims, user } = useAuth();
  const uid = user?.uid ?? null;
  const key = uid && claims ? scopeKey(claims, uid) : "";
  const [state, setState] = useState<{
    key: string;
    ready: boolean;
    rows: UserRow[];
    error: FirestoreError | null;
  }>({ key: "", ready: false, rows: [], error: null });

  useEffect(() => {
    if (!key || !uid) return;
    return onSnapshot(
      query(collection(db, COLLECTIONS.users), ...userScopeConstraints(claims, uid)),
      (snap) => {
        const rows = snap.docs.map((d) => ({ ...(d.data(SNAPSHOT_OPTIONS) as UserProfile), uid: d.id }));
        setState({ key, ready: true, rows, error: null });
      },
      (error) => setState({ key, ready: true, rows: [], error }),
    );
    // `key` covers claims/uid changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const current = state.key === key ? state : null;

  const activeOnly = opts?.activeOnly ?? false;
  const sourceRows = current?.rows;
  const users = useMemo(() => {
    const collator = new Intl.Collator(locale === "ar" ? "ar" : "en", { sensitivity: "base", numeric: true });
    const all = sourceRows ?? [];
    const rows = activeOnly ? all.filter((u) => u.active !== false) : all;
    return [...rows].sort((a, b) =>
      collator.compare(a.displayName || a.username || "", b.displayName || b.username || ""),
    );
  }, [sourceRows, activeOnly, locale]);

  return { users, loading: !current?.ready, error: current?.error ?? null };
}

/** Map uid -> user. */
export function indexUsers(users: UserRow[]): Map<string, UserRow> {
  return new Map(users.map((u) => [u.uid, u]));
}

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export interface AdminCreateUserInput {
  username: string;
  password: string;
  displayName: string;
  phone?: string;
  jobTitle?: string;
  role: Role;
  /** required for staff; admins may have no placement */
  companyId: string | null;
  administrationId?: string | null;
  departmentId?: string | null;
  /** staff / ID number (optional, <= 30) */
  staffNumber?: string;
  /** custom roles, each within the caller's own permissions (else permissionsExceedOwn) */
  roleIds?: string[];
  /** Super Admin only: the account starts verified (no SMS code or email link). */
  markVerified?: boolean;
  /** Other units besides the primary one (each a company, optionally an administration, optionally a department under it). */
  extraPlacements?: OrgPlacement[];
  /** Super Admin only: "Can see" units, or every company (seeAll). */
  viewUnitIds?: string[];
  seeAll?: boolean;
}

export interface AdminUpdateUserInput {
  uid: string;
  displayName?: string;
  phone?: string;
  jobTitle?: string;
  role?: Role;
  /**
   * Sending any of the three org ids replaces the whole placement (a level
   * that is not sent becomes null), so send all three together.
   */
  companyId?: string | null;
  administrationId?: string | null;
  departmentId?: string | null;
  /** optional password reset (signs the user out everywhere) */
  password?: string;
  /**
   * true: the employee must verify their account again (verified false, the
   * verified phone / email cleared, the phone number removed from their Auth
   * account). Use adminResetVerification().
   */
  resetVerification?: true;
  /** Super Admin only: verify the account now (no SMS code or email link). Use adminMarkVerified(). */
  markVerified?: true;
  staffNumber?: string;
  /** Replaces the user's custom roles; their permissions are recomputed by the function. */
  roleIds?: string[];
  /** Super Admin only: let this user past the network guard (VPN check). */
  vpnCheckBypass?: boolean;
  /** Replaces the other units ([] removes them). */
  extraPlacements?: OrgPlacement[];
  /** Super Admin only: replaces the "Can see" units / every company. */
  viewUnitIds?: string[];
  seeAll?: boolean;
}

export interface AdminSetUserActiveInput {
  uid: string;
  active: boolean;
}

// Created on first use so importing this module never touches Functions during prerender.
const createUserFn = (data: AdminCreateUserInput) => callable<AdminCreateUserInput, { uid: string }>("adminCreateUser")(data);
const updateUserFn = (data: AdminUpdateUserInput) => callable<AdminUpdateUserInput, { uid: string }>("adminUpdateUser")(data);
const setUserActiveFn = (data: AdminSetUserActiveInput) =>
  callable<AdminSetUserActiveInput, { uid: string; active: boolean }>("adminSetUserActive")(data);

/** Removes undefined keys (the callables reject unknown / undefined-valued keys cleanly). */
function compact<T extends object>(input: T): T {
  return Object.fromEntries(Object.entries(input).filter(([, v]) => v !== undefined)) as T;
}

/** Creates the Auth account + users/{uid} + claims. Resolves with the new uid. */
export function adminCreateUser(input: AdminCreateUserInput): Promise<{ uid: string }> {
  return createUserFn(
    compact({
      ...input,
      username: input.username.trim().toLowerCase(),
      displayName: input.displayName.trim(),
      phone: input.phone?.trim(),
      jobTitle: input.jobTitle?.trim(),
      staffNumber: input.staffNumber?.trim(),
      companyId: input.companyId || null,
      administrationId: input.administrationId || null,
      departmentId: input.departmentId || null,
    }),
  );
}

/** Updates profile / role / placement, optionally resets the password; claims are recomputed. */
export function adminUpdateUser(input: AdminUpdateUserInput): Promise<{ uid: string }> {
  const payload: AdminUpdateUserInput = compact({
    ...input,
    displayName: input.displayName?.trim(),
    phone: input.phone?.trim(),
    jobTitle: input.jobTitle?.trim(),
    staffNumber: input.staffNumber?.trim(),
    password: input.password ? input.password : undefined,
  });
  return updateUserFn(payload);
}

/** Makes the employee verify their account again (Staff page "Reset verification"). */
export function adminResetVerification(uid: string): Promise<{ uid: string }> {
  return updateUserFn({ uid, resetVerification: true });
}

/** A Super Admin verifies the employee's account on the spot (no SMS code or email link). */
export function adminMarkVerified(uid: string): Promise<{ uid: string }> {
  return updateUserFn({ uid, markVerified: true });
}

/** Enables / disables the account (a disabled user is signed out everywhere). */
export function adminSetUserActive(input: AdminSetUserActiveInput): Promise<{ uid: string; active: boolean }> {
  return setUserActiveFn(input);
}

/**
 * Deletes the account for good (employees.delete): the Auth user, users/{uid},
 * the photo and pending verification links. Their visits, customers, history
 * and login events stay, with the stored name. Refused for yourself
 * (cannotDeleteSelf), the last active Super Admin (lastSuperAdmin) and a
 * General Manager of any unit (userIsUnitHead).
 */
export function adminDeleteUser(uid: string): Promise<{ uid: string }> {
  return callable<{ uid: string }, { uid: string }>("adminDeleteUser")({ uid });
}

/** `details` of a callable HttpsError: { reason, field } (nulls when absent). */
export function callableErrorDetails(err: unknown): { reason: string | null; field: string | null } {
  const details = err && typeof err === "object" && "details" in err ? (err as { details: unknown }).details : null;
  if (!details || typeof details !== "object") return { reason: null, field: null };
  const d = details as { reason?: unknown; field?: unknown };
  return {
    reason: typeof d.reason === "string" ? d.reason : null,
    field: typeof d.field === "string" ? d.field : null,
  };
}
